import {
	Typography,
	TextField,
	Box,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Paper,
	SxProps,
	Theme,
} from '@mui/material'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CustomButton from '../../CustomButton/CustomButton'
import { useModal } from '../../../../../store/ModalProvider'
import { ErrorModalData } from '../../../../../types/modal'

/**
 * ## CustomModalError component
 * - @returns {JSX.Element} The rendered CustomModalError component
 */
const CustomModalError: React.FC = () => {
	const customeStyles: {
		dialog: SxProps<Theme>
		title: SxProps<Theme>
		subtitles: SxProps<Theme>
		content: SxProps<Theme>
		fields: SxProps<Theme>
	} = {
		dialog: {
			pointerEvents: 'auto',
			color: 'var(--primary-dark)',
			padding: '1.5rem 1rem',
		},
		title: {
			fontWeight: 600,
			fontSize: '1rem',
			fontFamily: 'var(--font-family)',
			color: 'var(--primary-dark)',
		},
		subtitles: {
			fontWeight: 600,
			fontSize: '14px',
			fontFamily: 'var(--font-family)',
			color: 'var(--primary-light)',
			lineHeight: '1.57',
		},
		content: {
			fontWeight: 600,
			fontSize: '14px',
			fontFamily: 'var(--font-family)',
			color: 'var(--primary-dark)',
			lineHeight: '1.57',
		},
		fields: {
			fontWeight: 500,
			fontFamily: 'var(--font-family)',
			fontSize: '13px',
			color: 'var(--primary-dark)',
			lineHeight: '1.3',
			padding: '0 12px',
			borderRadius: '4px',
			border: '1px solid rgba(134, 149, 175, 0)',
			background: 'var(--secondary-light)',
			outline: 'none',
			'& input': {
				fontFamily: 'var(--font-family)',
				color: 'var(--primary-dark)',
				padding: '1rem 0',
				fontSize: '14px',
			},
			'& textarea': {
				fontFamily: 'var(--font-family)',
				color: 'var(--primary-dark)',
				padding: '0',
				fontSize: '14px',
			},
			'& .MuiOutlinedInput-notchedOutline': {
				outline: 'none',
				border: 'none',
			},
			'& .MuiOutlinedInput-notchedOutline:focus': {
				outline: 'none',
				border: 'none',
			},
			'& .MuiOutlinedInput-notchedOutline:hover': {
				outline: 'none',
				border: 'none',
			},
			'& .MuiOutlinedInput-notchedOutline:active': {
				outline: 'none',
				border: 'none',
			},
			'& .MuiInputBase-multiline': {
				paddingInline: 0,
			},
		},
	}

	const { isOpen, closeModal, modalData, modalTitle } = useModal()

	if (!modalData) {
		return null
	}

	const { errorCode, errorMessage, exceptionMessage, exceptionSource, stackTrace } = modalData as ErrorModalData

	if (!errorCode || !errorMessage || !exceptionMessage || !exceptionSource || !stackTrace) {
		return null
	}

	const copyToClipboard = () => {
		const text = `Error Code: ${errorCode}\nError Message: ${errorMessage}\nException Message: ${exceptionMessage}\nException Source: ${exceptionSource}\nStack Trace: ${stackTrace}`
		navigator.clipboard.writeText(text).catch(err => {
			console.error('Could not copy text: ', err)
		})
	}

	return (
		<Dialog
			sx={{ ...customeStyles.dialog }}
			open={isOpen}
		>
			<Paper sx={{ minWidth: '600px' }}>
				<DialogTitle
					id='error-modal-title'
					variant='h6'
					component='h2'
					sx={{ ...customeStyles.title }}
				>
					{modalTitle}
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							position: 'relative',
							mb: 2,
							display: 'flex',
							alignItems: 'flex-start',
							gap: 1,
						}}
					>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-start',
									gap: '0.5rem',
								}}
							>
								<Typography sx={{ ...customeStyles.subtitles }}>Error Code: </Typography>
								<Typography
									variant='body1'
									sx={{ ...customeStyles.content }}
								>
									{errorCode}
								</Typography>
							</Box>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-start',
									gap: '0.5rem',
									marginBottom: '2.5rem',
								}}
							>
								<Typography sx={{ ...customeStyles.subtitles }}>Message: </Typography>
								<Typography
									variant='body1'
									sx={{ ...customeStyles.content }}
								>
									{errorMessage}
								</Typography>
							</Box>
						</Box>
						<IconButton
							onClick={copyToClipboard}
							aria-label='copy to clipboard'
							color='info'
							size='small'
							sx={{ marginLeft: 'auto' }}
						>
							<ContentCopyOutlinedIcon fontSize='small' />
						</IconButton>
					</Box>
					<Box sx={{ display: 'grid', gap: 2 }}>
						<TextField
							label='Exception Message'
							value={exceptionMessage}
							variant='outlined'
							fullWidth
							InputProps={{
								readOnly: true,
							}}
							sx={{ ...customeStyles.fields }}
						/>
						<TextField
							label='Exception Source'
							value={exceptionSource}
							variant='outlined'
							fullWidth
							InputProps={{
								readOnly: true,
							}}
							sx={{ ...customeStyles.fields }}
						/>
						<TextField
							label='Stack Trace'
							value={stackTrace}
							multiline
							rows={5}
							variant='outlined'
							fullWidth
							InputProps={{
								readOnly: true,
							}}
							sx={{ ...customeStyles.fields }}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<CustomButton
						size='small'
						variant='text'
						onClick={closeModal} // Call closeModal when Fechar is clicked
						sx={{ marginLeft: 'auto' }}
					>
						Fechar
					</CustomButton>
				</DialogActions>
			</Paper>
		</Dialog>
	)
}

export default CustomModalError
